import { useState } from 'react'
import { supabase } from '../lib/supabase'
import { useI18n } from '../i18n/useI18n'
import { useAuth } from '../auth/useAuth'
import { useAsync } from '../lib/useAsync'
import {
  defaultPeriodForYear, formatDate, formatMoney, formatPeriod, yearOfPeriod,
} from '../lib/format'
import { useSelectedYear } from '../year/useYear'
import { canApprove, canEnterData } from '../lib/types'
import type { Expense, ExpenseCategory, Unit } from '../lib/types'
import { Loading, ErrorState, EmptyState } from '../components/States'
import { ExpenseSheet } from './ExpenseSheet'
import { ApprovalSheet } from './ApprovalSheet'

function shiftPeriod(period: string, delta: number): string {
  const [y, m] = period.split('-').map(Number)
  const d = new Date(Date.UTC(y, m - 1 + delta, 1))
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`
}

interface ExpenseRow extends Expense {
  unit: { name: string } | null
}

async function load(period: string) {
  const { data, error } = await supabase
    .from('expenses')
    .select(
      'id, spent_on, period, category, unit_id, supplier, amount, paid_by,' +
        ' description, invoice_document, status, approved_by, approved_at,' +
        ' decision_reason, tenant_charge, unit:units(name)',
    )
    .eq('period', period)
    .order('spent_on', { ascending: false })
  if (error) throw error

  const { data: units } = await supabase
    .from('units')
    .select('id, name, kind, floor, notes, vacant_since, vacancy_reason')
    .order('name')

  return {
    expenses: (data ?? []) as unknown as ExpenseRow[],
    units: (units as Unit[]) ?? [],
  }
}

export function ExpensesScreen() {
  const { t, lang } = useI18n()
  const { profile } = useAuth()
  const { year } = useSelectedYear()
  // Comme pour les loyers, le mois reste borne a l'annee choisie.
  const [period, setPeriod] = useState(() => defaultPeriodForYear(year))
  const [sheet, setSheet] = useState<'closed' | 'new'>('closed')
  const [decide, setDecide] = useState<ExpenseRow | null>(null)

  const { data, loading, error, reload } = useAsync(() => load(period), [period])

  const peutSaisir = canEnterData(profile?.role ?? null)
  const peutApprouver = canApprove(profile?.role ?? null)

  const expenses = data?.expenses ?? []
  const approuve = expenses
    .filter((e) => e.status === 'approuve')
    .reduce((s, e) => s + e.amount, 0)
  const attente = expenses
    .filter((e) => e.status === 'en_attente')
    .reduce((s, e) => s + e.amount, 0)

  return (
    <div className="screen">
      <div className="screen__head">
        <h1 className="screen__title">{t('expenses_month')}</h1>
        {peutSaisir && (
          <button
            type="button"
            className="btn btn--primary btn--small"
            onClick={() => setSheet('new')}
          >
            {t('add')}
          </button>
        )}
      </div>

      <div className="monthnav">
        <button
          type="button"
          className="btn btn--ghost"
          disabled={period.endsWith('-01')}
          onClick={() => {
            const prev = shiftPeriod(period, -1)
            if (yearOfPeriod(prev) === year) setPeriod(prev)
          }}
        >
          ← {t('previous')}
        </button>
        <span className="monthnav__label">{formatPeriod(period, lang)}</span>
        <button
          type="button"
          className="btn btn--ghost"
          disabled={period.endsWith('-12')}
          onClick={() => {
            const next = shiftPeriod(period, 1)
            if (yearOfPeriod(next) === year) setPeriod(next)
          }}
        >
          {t('next')} →
        </button>
      </div>

      {loading && <Loading />}
      {error && <ErrorState message={error} onRetry={reload} />}

      {!loading && !error && expenses.length === 0 && (
        <EmptyState title={t('expenses_none')} />
      )}

      {expenses.length > 0 && (
        <div className="totals">
          <div>
            <span className="totals__label">{t('expense_total_approved')}</span>
            <span className="totals__value">{formatMoney(approuve)}</span>
          </div>
          <div>
            <span className="totals__label">{t('expense_total_pending')}</span>
            <span className="totals__value totals__value--warn">
              {formatMoney(attente)}
            </span>
          </div>
        </div>
      )}

      {expenses.length > 0 && (
        <ul className="list">
          {expenses.map((e) => (
            <li key={e.id}>
              <button
                type="button"
                className="list__item list__item--tappable"
                onClick={() => peutApprouver && setDecide(e)}
              >
                <span className="list__main">
                  <span className="list__name">
                    {t(`cat_${e.category}` as CatKey)}
                  </span>
                  <span className="list__meta">
                    {formatDate(e.spent_on)}
                    {e.unit?.name ? ` · ${e.unit.name}` : ` · ${t('expense_whole_building')}`}
                  </span>
                  {e.supplier && <span className="list__note">{e.supplier}</span>}
                  {e.tenant_charge && (
                    <span className="chip chip--warn">{t('tenant_charge_flag')}</span>
                  )}
                  {!e.invoice_document && (
                    <span className="list__note">{t('deposit_no_proof')}</span>
                  )}
                </span>
                <span className="list__side">
                  <span className="list__amount">{formatMoney(e.amount)}</span>
                  <span
                    className={
                      'chip ' +
                      (e.status === 'approuve'
                        ? 'chip--ok'
                        : e.status === 'refuse'
                          ? 'chip--error'
                          : 'chip--warn')
                    }
                  >
                    {t(`expense_status_${e.status}` as
                      'expense_status_en_attente' | 'expense_status_approuve'
                      | 'expense_status_refuse')}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {sheet === 'new' && (
        <ExpenseSheet
          period={period}
          units={data?.units ?? []}
          onClose={() => setSheet('closed')}
          onSaved={() => {
            setSheet('closed')
            reload()
          }}
        />
      )}

      {decide && (
        <ApprovalSheet
          expense={decide}
          unitName={decide.unit?.name ?? null}
          onClose={() => setDecide(null)}
          onSaved={() => {
            setDecide(null)
            reload()
          }}
        />
      )}
    </div>
  )
}

type CatKey =
  | 'cat_plomberie' | 'cat_vidange' | 'cat_eau' | 'cat_electricite'
  | 'cat_peinture' | 'cat_amende' | 'cat_menage' | 'cat_autre'

export type { ExpenseRow, ExpenseCategory }
